import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsersData, invalidateUsers } from '../../store/usersSlice';
import { getAuthTokenPayload } from '../../Utils/index';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Divider,
  Dialog,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Skeleton,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import DialogActions from '@mui/material/DialogActions';
import { alpha } from '@mui/material/styles';
import { userAction } from '../../Action/api';
import { format } from 'date-fns';
import AddUserDialog from './add';
import EditUserDialog from './edit';
// Remove the problematic imports and use direct localStorage checks
// import { usePermissionCheck } from '../../Context/PermissionGuard';
// import { PermissionGuard } from '../../Context/PermissionGuard';

export const Users = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // ── Redux: users list (cached across navigation) ───────────────────────────
  const users      = useSelector((state) => state.users.items);
  const usersStatus = useSelector((state) => state.users.status);
  const usersError  = useSelector((state) => state.users.error);

  // Derived flags – same semantics as the old useState variables
  const loading = usersStatus === 'loading' || usersStatus === 'idle';
  const error   = usersError;
  
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  // users / loading / error are now in Redux (see selectors above)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [statusChangeUser, setStatusChangeUser] = useState(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  // Permission check using menuList from JWT token
  const checkUserPermission = (menuId, action = 'read') => {
    try {
      const menuList = getAuthTokenPayload()?.menuList ?? [];
      const menu = menuList.find((m) => m.menuId === menuId);

      if (!menu) return false;

      switch (action) {
        case 'read':
        case 'view':
          return menu.readAccess === 'yes';
        case 'write':
        case 'add':
        case 'edit':
        case 'delete':
          return menu.writeAccess === 'yes';
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  };

  // Permission checks for Users menu (MENU-007)
  const canViewUsers = checkUserPermission('MENU-007', 'read');
  const canAddUser = checkUserPermission('MENU-007', 'write');
  const canEditUser = checkUserPermission('MENU-007', 'write');
  const canDeleteUser = checkUserPermission('MENU-007', 'write');
  const isCurrentUserAdmin = getAuthTokenPayload()?.roleName === 'Admin';

  // Extract userLimit from MENU-007 in the token's menuList
  const getUserLimit = () => {
    try {
      const menuList = getAuthTokenPayload()?.menuList ?? [];
      const usersMenu = menuList.find((m) => m.menuId === 'MENU-007');
      const limit = usersMenu?.userLimit;
      return limit !== undefined && limit !== null ? Number(limit) : null;
    } catch (e) {
      return null;
    }
  };
  const userLimit = getUserLimit();

  // True when a limit is defined and the current user count has reached it
  const isUserLimitReached = userLimit !== null && users.length >= userLimit;

  // Debug logs
  useEffect(() => {
    console.log('Users Permission Check:', {
      canView: canViewUsers,
      canAdd: canAddUser,
      canEdit: canEditUser,
      canDelete: canDeleteUser,
      menuList: getAuthTokenPayload()?.menuList ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only fetch users if user has read access and data not already in Redux
    if (canViewUsers) {
      if (usersStatus === 'idle') dispatch(fetchUsersData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewUsers]);

  // ── Data-fetch helper ─────────────────────────────────────────────────────
  /**
   * Force a fresh API call regardless of cache state (e.g. after a mutation).
   * On first mount we skip this and use the idle-check in useEffect instead.
   */
  const fetchUsers = () => {
    dispatch(invalidateUsers());
    dispatch(fetchUsersData());
  };

  const getPermissionDetails = (menuList, menuId) => {
    const menu = menuList?.find(m => m.menuId === menuId);
    if (!menu) return { hasAccess: false, readAccess: false, writeAccess: false };
    
    return {
      hasAccess: menu.readAccess === "yes" || menu.writeAccess === "yes",
      readAccess: menu.readAccess === "yes",
      writeAccess: menu.writeAccess === "yes",
      permission: menu.permission || "no"
    };
  };

  const handleAddUserClick = () => {
    if (!canAddUser) return;
    if (isUserLimitReached) {
      setSnackbar({
        open: true,
        message: `User limit reached. Your current subscription allows a maximum of ${userLimit} user${userLimit === 1 ? '' : 's'}.`,
        severity: 'warning'
      });
      return;
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAddUserSuccess = (userName, userType) => {
    setSnackbar({
      open: true,
      message: `Successfully added ${userName} as a new ${userType.toLowerCase()}`,
      severity: 'success'
    });
    fetchUsers();
  };

  const handleEditClick = (user) => {
    if (canEditUser) {
      setSelectedUserData(user);
      setOpenEditDialog(true);
    }
  };

  const handleEditSuccess = (userName) => {
    setSnackbar({
      open: true,
      message: `Successfully updated ${userName}`,
      severity: 'success'
    });
    fetchUsers();
  };

  const handleStatusChange = (user) => {
    if (canEditUser) {
      setStatusChangeUser(user);
      setOpenConfirmDialog(true);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteUserTarget(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await userAction(deleteUserTarget.userId, 'remove');
      if (response?.data?.status || response?.data?.status === true) {
        setSnackbar({
          open: true,
          message: `Successfully removed ${deleteUserTarget.staffFullName}`,
          severity: 'success'
        });
        fetchUsers();
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.message || 'Failed to remove user. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error removing user:', error);
      setSnackbar({ open: true, message: 'An error occurred while removing the user.', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setDeleteUserTarget(null);
    }
  };

  const handleStatusChangeConfirm = async () => {
    setIsStatusUpdating(true);
    try {
      const action = statusChangeUser.userStatus === 'Active' ? 'deactivate' : 'activate';
      const newStatus = action === 'activate' ? 'Active' : 'Inactive';
      const response = await userAction(statusChangeUser.userId, action);

      if (response?.data?.status || response?.data?.status === true) {
        setSnackbar({
          open: true,
          message: `Successfully ${newStatus === 'Active' ? 'activated' : 'deactivated'} ${statusChangeUser.staffFullName}`,
          severity: 'success'
        });
        fetchUsers();
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.message || 'Failed to update user status',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while updating user status',
        severity: 'error'
      });
    } finally {
      setIsStatusUpdating(false);
      setOpenConfirmDialog(false);
      setStatusChangeUser(null);
    }
  };

  // If user doesn't have read access, show access denied message
  if (!canViewUsers) {
    return (
      <Box sx={{ py: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Container maxWidth="xl">
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Access Denied
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                You don't have permission to view the Users module. Please contact your administrator for access.
              </Typography>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ 
          mb: { xs: 3, sm: 4 }, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: '#1e293b',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}>
                Users Management
              </Typography>
              {/* Show access level indicator */}
              <Chip 
                label={canAddUser ? "Full Access" : "Read Only"} 
                size="small" 
                sx={{ 
                  bgcolor: canAddUser ? '#dcfce7' : '#fef3c7',
                  color: canAddUser ? '#15803d' : '#d97706',
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              />
            </Box>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '0.875rem', md: '1rem' }
              }}
            >
              {canAddUser 
                ? "Manage user access and permissions" 
                : "View user access and permissions (Read-only)"
              }
            </Typography>
          </Box>
          {/* Only show Add User button if user has write access */}
          {canAddUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1, minWidth: { sm: 200 } }}>

              {/* ── Usage counter + progress bar (always visible when limit is set) ── */}
              {userLimit !== null && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: isUserLimitReached ? '#fff7ed' : '#f1f5f9',
                  border: '1px solid',
                  borderColor: isUserLimitReached ? '#fdba74' : '#e2e8f0',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GroupIcon sx={{ fontSize: 15, color: isUserLimitReached ? '#ea580c' : '#64748b' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: isUserLimitReached ? '#ea580c' : '#334155', fontSize: '0.75rem' }}>
                        Users
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isUserLimitReached ? '#ea580c' : '#1e293b', fontSize: '0.75rem' }}>
                      {users.length} / {userLimit}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((users.length / userLimit) * 100, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: isUserLimitReached ? '#ef4444' : '#4B5AB6',
                      }
                    }}
                  />
                  {isUserLimitReached && (
                    <Typography variant="caption" sx={{ color: '#ea580c', fontSize: '0.7rem', fontWeight: 500 }}>
                      Subscription limit reached
                    </Typography>
                  )}
                </Box>
              )}

              {/* ── Add New User button ── */}
              <Button
                variant="contained"
                startIcon={isUserLimitReached ? <LockIcon /> : (!isMobile && <PersonAddIcon />)}
                onClick={handleAddUserClick}
                disabled={isUserLimitReached}
                fullWidth
                sx={{
                  bgcolor: isUserLimitReached ? undefined : '#4B5AB6',
                  '&:hover': { bgcolor: isUserLimitReached ? undefined : '#3a4891' },
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.2, sm: 1 },
                  whiteSpace: 'nowrap'
                }}
              >
                {isUserLimitReached ? 'Limit Reached' : (isMobile ? <><PersonAddIcon sx={{ mr: 1 }} /> Add User</> : 'Add New User')}
              </Button>

              {/* ── Inline upgrade nudge when limit is hit ── */}
              {isUserLimitReached && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 2,
                    fontSize: '0.72rem',
                    '& .MuiAlert-message': { p: 0 }
                  }}
                >
                  <AlertTitle sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.25 }}>User limit reached</AlertTitle>
                  Your plan allows <strong>{userLimit}</strong> user{userLimit === 1 ? '' : 's'}.
                  Upgrade your subscription to add more.
                </Alert>
              )}

            </Box>
          )}
        </Box>

        {/* Search and Filter Section - Show for all users but limit functionality */}
        <Paper sx={{ p: { xs: 2, sm: 2 }, mb: { xs: 3, sm: 4 }, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                placeholder={isMobile ? "Search..." : "Search users..."}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1, fontSize: { xs: 18, sm: 24 } }} />,
                }}
                sx={{ bgcolor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {/* <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
                >
                  Filter
                </Button> */}
                {/* Only show export for write access users */}
                {/* {canAddUser && (
                  <Button
                    variant="outlined"
                    sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
                  >
                    Export Users
                  </Button>
                )} */}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Users Table - Desktop View */}
        {!isMobile ? (
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 3, 
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                  {/* Only show Actions column if user has write access */}
                  {canEditUser && (
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
              {(() => {
                if (loading) {
                  return [...Array(4)].map((_, index) => (
                    <TableRow key={index}>
                      {[...Array(canEditUser ? 6 : 5)].map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton animation="wave" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ));
                }

                if (error) {
                  return (
                    <TableRow>
                      <TableCell colSpan={canEditUser ? 6 : 5} align="center" sx={{ py: 3 }}>
                        <Typography color="error">{error}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                return users.map((user) => (
                  <TableRow 
                    key={user.userId}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#4f46e5' }}>
                          {user.staffFullName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.staffFullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.userId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.roleName || 'User'}
                        size="small"
                        sx={{
                          bgcolor: (user.roleName === 'Admin') ? '#fee2e2' : '#f0fdf4',
                          color: (user.roleName === 'Admin') ? '#dc2626' : '#059669',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Your existing permissions display code */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 400 }}>
                        {[
                          { id: 'MENU-001', name: 'Dashboard' },
                          { id: 'MENU-002', name: 'RunPost' },
                          { id: 'MENU-003', name: 'SafePost' },
                          { id: 'MENU-004', name: 'EmailCampaign' },
                          { id: 'MENU-005', name: 'SMSCampaign' },
                          { id: 'MENU-006', name: 'Approvals' },
                          { id: 'MENU-007', name: 'Users' },
                          { id: 'MENU-008', name: 'Settings' },
                          { id: 'MENU-009', name: 'MarketingAssets' },
                          { id: 'MENU-010', name: 'Secure Accounts' },
                          { id: 'MENU-011', name: 'Calendar Schedule' }
                        ].map((menu) => {
                          const permissionDetails = getPermissionDetails(user.menuList, menu.id);
                          
                          if (!permissionDetails.hasAccess) return null;
                          
                          return (
                            <Tooltip 
                              key={menu.id} 
                              title={
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {menu.name} Permissions:
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      • Read: {permissionDetails.readAccess ? '✓ Yes' : '✗ No'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      • Write: {permissionDetails.writeAccess ? '✓ Yes' : '✗ No'}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              placement="top"
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 0.5,
                                backgroundColor: permissionDetails.writeAccess 
                                  ? '#fef3c7'
                                  : '#f0fdf4',
                                padding: '4px 8px',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: permissionDetails.writeAccess 
                                  ? '#f59e0b'
                                  : '#86efac',
                                minWidth: 'auto',
                                cursor: 'help'
                              }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: permissionDetails.writeAccess 
                                      ? '#d97706'
                                      : '#15803d',
                                    fontSize: '0.7rem',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {menu.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                  {permissionDetails.readAccess && (
                                    <Box
                                      sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: '#10b981',
                                        title: 'Read Access'
                                      }}
                                    />
                                  )}
                                  {permissionDetails.writeAccess && (
                                    <Box
                                      sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: '#f59e0b',
                                        title: 'Write Access'
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        }).filter(Boolean)}
                        
                        {![
                          'MENU-001', 'MENU-002', 'MENU-003', 'MENU-004',
                          'MENU-005', 'MENU-006', 'MENU-007', 'MENU-008', 'MENU-009', 'MENU-010', 'MENU-011'
                        ].some(menuId => getPermissionDetails(user.menuList, menuId).hasAccess) && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: '#fef2f2',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            border: '1px solid #fecaca',
                          }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 600,
                                color: '#dc2626',
                                fontSize: '0.7rem'
                              }}
                            >
                              No Permissions
                            </Typography>
                            <CloseIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.userStatus || 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: user.userStatus === 'Active' ? '#f0fdf4' : '#fef2f2',
                          color: user.userStatus === 'Active' ? '#15803d' : '#dc2626',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(user.createdAt), 'dd MMM yyyy')}
                      </Typography>
                    </TableCell>
                    {/* Inline action buttons */}
                    {canEditUser && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(user)}
                              sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.roleName !== 'Admin' && (
                            <Tooltip title={user.userStatus === 'Active' ? 'Deactivate User' : 'Activate User'}>
                              <IconButton
                                size="small"
                                onClick={() => handleStatusChange(user)}
                                sx={{
                                  color: user.userStatus === 'Active' ? '#dc2626' : '#059669',
                                  '&:hover': { bgcolor: user.userStatus === 'Active' ? '#fef2f2' : '#f0fdf4' }
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isCurrentUserAdmin && user.roleName !== 'Admin' && (
                            <Tooltip title="Remove User">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(user)}
                                sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        ) : (
          /* Mobile Card View */
          <Box>
            {(() => {
              if (loading) {
                return [...Array(3)].map((_, index) => (
                  <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Skeleton variant="circular" width={48} height={48} />
                      <Skeleton variant="text" sx={{ mt: 1 }} />
                      <Skeleton variant="text" width="60%" />
                    </CardContent>
                  </Card>
                ));
              }

              if (error) {
                return (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                );
              }

              return users.map((user) => (
                <Card 
                  key={user.userId}
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* User Info Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: '#4f46e5', width: 48, height: 48 }}>
                          {user.staffFullName?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }} noWrap>
                            {user.staffFullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {user.userId}
                          </Typography>
                        </Box>
                      </Box>
                      {canEditUser && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 1, flexShrink: 0 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(user)}
                              sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {user.roleName !== 'Admin' && (
                            <Tooltip title={user.userStatus === 'Active' ? 'Deactivate' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={() => handleStatusChange(user)}
                                sx={{
                                  color: user.userStatus === 'Active' ? '#dc2626' : '#059669',
                                  '&:hover': { bgcolor: user.userStatus === 'Active' ? '#fef2f2' : '#f0fdf4' }
                                }}
                              >
                                <BlockIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isCurrentUserAdmin && user.roleName !== 'Admin' && (
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(user)}
                                sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* User Details */}
                    <Stack spacing={1.5}>
                      {/* Role */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Role
                        </Typography>
                        <Chip 
                          label={user.roleName || 'User'}
                          size="small"
                          sx={{
                            bgcolor: (user.roleName === 'Admin') ? '#fee2e2' : '#f0fdf4',
                            color: (user.roleName === 'Admin') ? '#dc2626' : '#059669',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>

                      {/* Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Status
                        </Typography>
                        <Chip 
                          label={user.userStatus || 'Inactive'}
                          size="small"
                          sx={{
                            bgcolor: user.userStatus === 'Active' ? '#f0fdf4' : '#fef2f2',
                            color: user.userStatus === 'Active' ? '#15803d' : '#dc2626',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>

                      {/* Created Date */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Created
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(user.createdAt), 'dd MMM yyyy')}
                        </Typography>
                      </Box>

                      {/* Permissions */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          Permissions
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {[
                            { id: 'MENU-001', name: 'Dashboard' },
                            { id: 'MENU-002', name: 'RunPost' },
                            { id: 'MENU-003', name: 'SafePost' },
                            { id: 'MENU-004', name: 'Email' },
                            { id: 'MENU-005', name: 'SMS' },
                            { id: 'MENU-006', name: 'Approvals' },
                            { id: 'MENU-007', name: 'Users' },
                            { id: 'MENU-008', name: 'Settings' },
                            { id: 'MENU-009', name: 'Assets' }
                          ].map((menu) => {
                            const permissionDetails = getPermissionDetails(user.menuList, menu.id);
                            
                            if (!permissionDetails.hasAccess) return null;
                            
                            return (
                              <Chip
                                key={menu.id}
                                label={menu.name}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 22,
                                  bgcolor: permissionDetails.writeAccess ? '#fef3c7' : '#f0fdf4',
                                  color: permissionDetails.writeAccess ? '#d97706' : '#15803d',
                                  fontWeight: 500,
                                }}
                              />
                            );
                          }).filter(Boolean)}
                          
                          {![
                            'MENU-001', 'MENU-002', 'MENU-003', 'MENU-004',
                            'MENU-005', 'MENU-006', 'MENU-007', 'MENU-008', 'MENU-009', 'MENU-010'
                          ].some(menuId => getPermissionDetails(user.menuList, menuId).hasAccess) && (
                            <Chip
                              label="No Permissions"
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                bgcolor: '#fef2f2',
                                color: '#dc2626',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ));
            })()}
          </Box>
        )}

        {/* Only render dialogs if user has write access */}
        {canAddUser && (
          <AddUserDialog 
            open={openDialog}
            onClose={handleDialogClose}
            onSuccess={handleAddUserSuccess}
          />
        )}

        {canEditUser && (
          <>
            <EditUserDialog 
              open={openEditDialog}
              onClose={() => setOpenEditDialog(false)}
              onSuccess={handleEditSuccess}
              userData={selectedUserData}
            />

            {/* Confirmation Dialog for status change */}
            <Dialog
              open={openConfirmDialog}
              onClose={() => {
                setOpenConfirmDialog(false);
                setStatusChangeUser(null);
              }}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
                }
              }}
            >
              <Box sx={{ 
                background: statusChangeUser?.userStatus === 'Active' ? '#dc2626' : '#059669',
                pt: 3, 
                pb: 4,
                px: 3,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
                }} />
                <Typography variant="h5" sx={{ 
                  color: 'white',
                  fontWeight: 700,
                  position: 'relative',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <BlockIcon />
                  {statusChangeUser?.userStatus === 'Active' ? 'Deactivate' : 'Activate'} User
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: alpha('#fff', 0.7),
                  position: 'relative'
                }}>
                  Are you sure you want to {statusChangeUser?.userStatus === 'Active' ? 'deactivate' : 'activate'} {statusChangeUser?.staffFullName}?
                </Typography>
              </Box>

              <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button 
                  onClick={() => {
                    setOpenConfirmDialog(false);
                    setStatusChangeUser(null);
                  }}
                  disabled={isStatusUpdating}
                  sx={{ 
                    color: '#64748b',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleStatusChangeConfirm}
                  disabled={isStatusUpdating}
                  sx={{
                    bgcolor: statusChangeUser?.userStatus === 'Active' ? '#dc2626' : '#059669',
                    '&:hover': { 
                      bgcolor: statusChangeUser?.userStatus === 'Active' ? '#b91c1c' : '#047857'
                    },
                    minWidth: 100
                  }}
                >
                  {isStatusUpdating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      <span>Updating...</span>
                    </Box>
                  ) : (
                    statusChangeUser?.userStatus === 'Active' ? 'Deactivate' : 'Activate'
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* Delete User Confirmation Dialog - Admin only */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => { setOpenDeleteDialog(false); setDeleteUserTarget(null); }}
          PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)', maxWidth: 420 } }}
        >
          <Box sx={{
            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
            pt: 3, pb: 4, px: 3,
            position: 'relative', overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)'
            }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, position: 'relative' }}>
              <DeleteOutlineIcon sx={{ color: 'white', fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                Remove User
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), position: 'relative' }}>
              This will permanently remove <strong>{deleteUserTarget?.staffFullName}</strong> from the system. This action cannot be undone.
            </Typography>
          </Box>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button
              onClick={() => { setOpenDeleteDialog(false); setDeleteUserTarget(null); }}
              variant="outlined"
              sx={{ color: '#64748b', borderColor: '#e2e8f0', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, px: 3 }}
            >
              Remove User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              '& .MuiAlert-icon': {
                fontSize: 24
              },
              '& .MuiAlert-message': {
                fontSize: '0.95rem'
              }
            }}
            iconMapping={{
              success: <CheckCircleIcon fontSize="inherit" />
            }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Users;